<!--
  @component LifecycleIO — P1 control-plane lens.
  chain:   LIFECYCLE_PHASES (network/deployment@1.9.0 truth) → this → ctx.lifecycle
  reads:   the static lifecycle truth shipped with the engine
           (k·L·m·n·o·p·q·r — 8/8 ENFORCE PERMANENT at v1.9.0).
  effects: NONE — board projection only.

  A live meta-bar feed (replacing the constant with streamed verdicts)
  lands in P2+ when the command channel is wired. Until then this is
  the same honest staged-surface discipline as the rest of the node:
  current truth, not a stub.
-->

<script lang="ts" module>
  import { getContext } from "svelte";
  import { LIFECYCLE_IO_KEY } from "./deployment-io.js";
  import type { LifecycleIOContext } from "./deployment-io.js";

  export { LIFECYCLE_IO_KEY };
  export type { LifecycleIOContext };

  export function useLifecycleIO(): LifecycleIOContext {
    const ctx = getContext<LifecycleIOContext | undefined>(LIFECYCLE_IO_KEY);
    if (!ctx) {
      throw new Error(
        "useLifecycleIO() called outside of a LifecycleIO tree.",
      );
    }
    return ctx;
  }
</script>

<script lang="ts">
  import { setContext, type Snippet } from "svelte";
  import { useDeploymentIO } from "./DeploymentIO.svelte";
  import { LIFECYCLE_PHASES } from "./deployment-io.js";

  interface Props {
    children?: Snippet;
  }
  const { children }: Props = $props();

  // Enforce mount-inside-DeploymentIO discipline (the slice rule);
  // P2+ will start consuming `parent.stream` for live verdicts.
  useDeploymentIO();

  const phases = LIFECYCLE_PHASES;
  const completeCount = phases.filter(
    (p) => p.status === "COMPLETE",
  ).length;

  const ctx: LifecycleIOContext = {
    phases,
    completeCount,
    total: phases.length,
    assertedFrom:
      "network/deployment@1.9.0 (8/8 ENFORCE PERMANENT; trigger 4e040ad)",
  } satisfies LifecycleIOContext;

  setContext(LIFECYCLE_IO_KEY, ctx);
</script>

{@render children?.()}
